import type { InstructionBlockKind } from '@onefootprint/request-types/smart-blocks';
import type { ReactNode } from 'react';
import type {
  BlockFenceEditOptions,
  BlockFenceEntry,
  BlockFenceRegistry,
} from '@/components/block-primitives/block-fence-registry';
import {
  type AgentChecklistBlockConfig,
  type AgentProcedureBlockConfig,
  parseChecklistConfig,
  parseInstructionChecklistConfig,
  parseInstructionProcedureConfig,
  parseProcedureConfig,
} from '@/components/block-primitives/contracts/agent-blocks';
import AgentChecklistBlock from './agent-checklist-block';
import AgentProcedureBlock from './agent-procedure-block';

const defineBlock = <Config,>(entry: {
  parse: (raw: string) => Config | null;
  render: (
    config: Config,
    options: Omit<BlockFenceEditOptions, 'onChange'> & { onChange: (config: Config) => void },
  ) => ReactNode;
}) => entry;

// Only instruction blocks are rejected at publish when empty, so only they warn about it.
const checklistEntry = (parse: (raw: string) => AgentChecklistBlockConfig | null, requireItems: boolean) =>
  defineBlock({
    parse,
    render: (config, { editable, justInserted, onChange, onDelete, onExit }) => (
      <AgentChecklistBlock
        config={config}
        editable={editable}
        justInserted={justInserted}
        onChange={onChange}
        onDelete={onDelete}
        onExit={onExit}
        requireItems={requireItems}
      />
    ),
  });

const procedureEntry = (parse: (raw: string) => AgentProcedureBlockConfig | null, requireSteps: boolean) =>
  defineBlock({
    parse,
    render: (config, { editable, justInserted, onChange, onDelete, onExit }) => (
      <AgentProcedureBlock
        config={config}
        editable={editable}
        justInserted={justInserted}
        onChange={onChange}
        onDelete={onDelete}
        onExit={onExit}
        requireSteps={requireSteps}
      />
    ),
  });

const agentBlockRegistry = {
  checklist: checklistEntry(parseChecklistConfig, false),
  procedure: procedureEntry(parseProcedureConfig, false),
} satisfies BlockFenceRegistry;

const instructionBlockRegistry = {
  checklist: checklistEntry(parseInstructionChecklistConfig, true),
  procedure: procedureEntry(parseInstructionProcedureConfig, true),
} satisfies Record<InstructionBlockKind, BlockFenceEntry>;

export { agentBlockRegistry, instructionBlockRegistry };
